import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recruitmentCompanyVacancyService } from '../../services/recruitmentCompanyVacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import Loader from '../../components/Loader';
import { STORAGE_BASE_URL } from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';

const RecruitmentCompanyVacancyApplications = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t } = useTranslation();
    const [applications, setApplications] = useState([]);
    const [vacancy, setVacancy] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadApplications();
        loadVacancy();
    }, [id]);

    const loadVacancy = async () => {
        try {
            const response = await recruitmentCompanyVacancyService.getById(id);
            if (response.success) {
                setVacancy(response.data);
            } else if (response?.data) {
                setVacancy(response.data);
            }
        } catch (error) {
            console.error('Failed to load vacancy');
        }
    };

    const loadApplications = async () => {
        try {
            setLoading(true);
            const response = await recruitmentCompanyVacancyService.getApplications(id);
            if (response.success) {
                setApplications(response.data || []);
            } else if (response?.data) {
                setApplications(Array.isArray(response.data) ? response.data : []);
            } else if (Array.isArray(response)) {
                setApplications(response);
            }
        } catch (error) {
            toast.error('فشل تحميل الطلبات');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (applicationId, newStatus) => {
        try {
            const response = await recruitmentCompanyVacancyService.changeApplicationStatus(id, applicationId, newStatus);
            if (response.success) {
                toast.success('تم تحديث حالة الطلب');
                loadApplications();
            }
        } catch (error) {
            toast.error('فشل تحديث حالة الطلب');
        }
    };

    const downloadCv = (application) => {
        try {
            const raw = application.cv;
            if (!raw) {
                toast.error('لا توجد سيرة ذاتية متاحة');
                return;
            }
            const url = raw.startsWith('http') ? raw : `${STORAGE_BASE_URL}/${raw}`;
            const filenameFromUrl = url.split('/').pop() || `cv_${application.id}.pdf`;
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', filenameFromUrl);
            a.setAttribute('target', '_blank');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            toast.error('تعذر تنزيل السيرة الذاتية');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'غير محدد';
        const date = new Date(dateString);
        const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending': 'قيد الانتظار',
            'viewed': 'تم المشاهدة',
            'accepted': 'مقبول',
            'rejected': 'مرفوض'
        };
        return labels[status] || status;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'accepted':
                return 'status-badge-accepted';
            case 'rejected':
                return 'status-badge-rejected';
            case 'viewed':
                return 'status-badge-viewed';
            default:
                return 'status-badge-pending';
        }
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    return (
        <div className="employee-home-page" dir="rtl">
            <RecruitmentCompanyNav user={user} />

            {/* Header Section */}
            <div className="px-3 mb-4 mt-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="back-btn"
                        style={{
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h3 className="section-title mb-1">طلبات الوظيفة</h3>
                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                            {vacancy?.job_title?.name || 'الوظيفة'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Applications List */}
            <div className="all-jobs-section px-3 mb-5">
                {applications.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="empty-state-text">لم يتم استلام أي طلبات بعد</p>
                    </div>
                ) : (
                    <div className="jobs-list">
                        {applications.map(application => {
                            const employee = application.employee;
                            const employeeUser = employee?.user;
                            const employeeImage = employeeUser?.image;
                            const employeeImageUrl = employeeImage
                                ? (employeeImage.startsWith('http') ? employeeImage : `${STORAGE_BASE_URL}/${employeeImage}`)
                                : null;

                            return (
                                <div key={application.id} className="job-card mb-3">
                                    <div className="d-flex align-items-start gap-3">
                                        <div className="company-logo-card">
                                            {employeeImageUrl ? (
                                                <img
                                                    src={employeeImageUrl}
                                                    alt="Employee"
                                                    className="logo-img"
                                                />
                                            ) : (
                                                <div className="company-logo-text text-dark align-content-center">
                                                    <b>
                                                        {employeeUser?.name?.charAt(0).toUpperCase() || 'E'}
                                                    </b>
                                                </div>
                                            )}
                                        </div>
                                        <div className="job-content flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <div className="job-title-card mb-1">
                                                        {employeeUser?.name || 'غير محدد'}
                                                    </div>
                                                    <div className="job-company-location mb-2">
                                                        {employeeUser?.email || 'غير محدد'}
                                                    </div>
                                                    {employee?.job_title && (
                                                        <div className="job-company-location mb-2">
                                                            {employee.job_title.name}
                                                        </div>
                                                    )}
                                                    <div className="job-tags d-flex gap-2 mb-2">
                                                        <span className={`status-badge ${getStatusBadgeClass(application.status)}`}>
                                                            {getStatusLabel(application.status)}
                                                        </span>
                                                        {application.applied_at && (
                                                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                {formatDate(application.applied_at)} - {formatTime(application.applied_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {application.notes && (
                                                        <div className="mb-2" style={{ fontSize: '0.9rem', color: '#666' }}>
                                                            <strong>ملاحظات:</strong> {application.notes}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 flex-wrap">
                                                {employee?.id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/recruitment-company/employees/${employee.id}`)}
                                                        className="btn-view-vacancy"
                                                        style={{
                                                            background: '#4a7bc4',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '0.5rem',
                                                            padding: '0.5rem 1rem',
                                                            fontSize: '0.875rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.5rem'
                                                        }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                            <circle cx="12" cy="7" r="4"></circle>
                                                        </svg>
                                                        {t('company.vacancyApplications.viewProfile') || 'View Profile'}
                                                    </button>
                                                )}
                                                {application.cv && (
                                                    <>
                                                        <a
                                                            href={application.cv.startsWith('http')
                                                                ? application.cv
                                                                : `${STORAGE_BASE_URL}/${application.cv}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn-view-vacancy status-badge status-badge-info"
                                                            style={{ marginInlineEnd: 8 }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                <circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadCv(application)}
                                                            className="btn-view-vacancy status-badge status-badge-success"
                                                        >
                                                            <svg width="24" height="24" viewBox="0 0 36 36" fill="currentColor" version="1.1" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Install icon" className="clr-i-outline clr-i-outline-path-1">
                                                                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                                <g id="SVGRepo_iconCarrier">
                                                                    <title>install-line</title>
                                                                    <path
                                                                        className="clr-i-outline clr-i-outline-path-1"
                                                                        d="M30.92,8H26.55a1,1,0,0,0,0,2H31V30H5V10H9.38a1,1,0,0,0,0-2H5.08A2,2,0,0,0,3,10V30a2,2,0,0,0,2.08,2H30.92A2,2,0,0,0,33,30V10A2,2,0,0,0,30.92,8Z"
                                                                    />
                                                                    <path
                                                                        className="clr-i-outline clr-i-outline-path-2"
                                                                        d="M10.3,18.87l7,6.89a1,1,0,0,0,1.4,0l7-6.89a1,1,0,0,0-1.4-1.43L19,22.65V4a1,1,0,0,0-2,0V22.65l-5.3-5.21a1,1,0,0,0-1.4,1.43Z"
                                                                    />
                                                                    <rect x="0" y="0" width="36" height="36" fillOpacity="0" />
                                                                </g>
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                                <select
                                                    value={application.status}
                                                    onChange={(e) => handleStatusChange(application.id, e.target.value)}
                                                    className="edit-profile-input"
                                                    style={{
                                                        minWidth: '150px',
                                                        padding: '0.5rem 1rem',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    <option value="pending">قيد الانتظار</option>
                                                    <option value="viewed">تم المشاهدة</option>
                                                    <option value="accepted">مقبول</option>
                                                    <option value="rejected">مرفوض</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <RecruitmentCompanyBottomNavigation />
        </div>
    );
};

export default RecruitmentCompanyVacancyApplications;

